/**
 * SpotChatWidget
 *
 * Anonymous sales/support chat with Spot the mascot on Bootcamp course
 * pages. After a short delay (default 20s), Spot's speech
 * bubble invites questions. Clicking opens a compact chat panel backed by
 * POST /public/spot-chat.
 * The panel close control returns to the mascot launcher without clearing the
 * conversation. The launcher's separate close control dismisses the entire
 * widget for the current page lifetime; a browser refresh restores it.
 *
 * WHY: Visitors should not have to read the whole page to get answers.
 * No login required. The current rendered page is captured as normalized text,
 * links, and image descriptions when a question is sent. Raw HTML is never
 * sent to Node or the model.
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import spotCircle from '../../assets/spot-circle.png';
import {
  getSpotBubbleDelayMs,
  SpotPageSnapshot,
  SpotChatMessage,
  streamSpotChat,
} from './spotChatApi';
import './SpotChatWidget.css';

type UiMessage =
  | SpotChatMessage
  | { role: 'error'; content: string }
  | { role: 'assistant'; content: string; streaming?: boolean };

/** Pages where Spot appears and receives current rendered-page context. */
export const SPOT_ALLOWED_PATH_PREFIXES = ['/courses/ai-trading-bootcamp'];

export function isSpotAllowedPath(pathname: string): boolean {
  return SPOT_ALLOWED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

const normalizePageLines = (value: string): string[] => {
  const seen = new Set<string>();
  return value
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => {
      if (!line || seen.has(line)) return false;
      seen.add(line);
      return true;
    });
};

const elementIsRendered = (element: Element): boolean => {
  if (
    element.closest(
      '[data-testid="spot-chat-widget"], [hidden], [aria-hidden="true"], script, style, noscript, template',
    )
  ) {
    return false;
  }
  if (!window.getComputedStyle) {
    return true;
  }
  let current: Element | null = element;
  while (current) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    current = current.parentElement;
  }
  return true;
};

/**
 * Capture what the visitor can read now, not the page HTML or a duplicated
 * course summary. Exact duplicate lines are removed to keep prompt tokens low.
 */
export function collectVisiblePageSnapshot(): SpotPageSnapshot {
  if (!document.body) {
    return { text: '', links: [], imageDescriptions: [] };
  }

  const textWalker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const visibleTextParts: string[] = [];
  let textNode = textWalker.nextNode();
  while (textNode) {
    const parent = textNode.parentElement;
    if (parent && elementIsRendered(parent)) {
      visibleTextParts.push(textNode.textContent || '');
    }
    textNode = textWalker.nextNode();
  }
  const text = normalizePageLines(visibleTextParts.join('\n')).join('\n');

  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]'))
    .filter(elementIsRendered)
    .map((link) => ({
      label: (link.innerText || link.textContent || '').replace(/\s+/g, ' ').trim(),
      href: link.href,
    }))
    .filter((link) => link.label && link.href)
    .filter(
      (link, index, all) =>
        all.findIndex(
          (candidate) => candidate.label === link.label && candidate.href === link.href,
        ) === index,
    );

  const imageDescriptions = Array.from(document.querySelectorAll<HTMLImageElement>('img[alt]'))
    .filter(elementIsRendered)
    .map((image) => image.alt.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .filter((description, index, all) => all.indexOf(description) === index);

  return { text, links, imageDescriptions };
}

/**
 * Invitations that cycle in Spot's speech bubble. Each one animates in, sits
 * for a few seconds, then animates out so the bubble feels alive and clearly
 * interactive instead of a static label.
 */
export const SPOT_BUBBLE_MESSAGES = [
  'Hey, do you have any questions? I can help.',
  'Curious about the price or the schedule? Just ask.',
  'Not sure the bootcamp is for you? Let\u2019s find out.',
];

function createSessionId(): string {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `spot-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Render simple markdown links without pulling in a full markdown library. */
function renderMessageContent(content: string): React.ReactNode {
  const parts = content.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, index) => {
    const match = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!match) {
      return <React.Fragment key={`t-${index}`}>{part}</React.Fragment>;
    }
    const [, label, href] = match;
    const safeHref =
      href.startsWith('/') || href.startsWith('https://') || href.startsWith('http://')
        ? href
        : '#';
    return (
      <a
        key={`a-${index}`}
        href={safeHref}
        target={safeHref.startsWith('/') ? undefined : '_blank'}
        rel={safeHref.startsWith('/') ? undefined : 'noreferrer'}
      >
        {label}
      </a>
    );
  });
}

export type SpotChatWidgetProps = {
  /** Override bubble delay for tests / experiments (ms). */
  bubbleDelayMs?: number;
  /** Force open for tests. */
  defaultOpen?: boolean;
  /** Force bubble visible immediately (tests). */
  forceBubble?: boolean;
};

export default function SpotChatWidget({
  bubbleDelayMs,
  defaultOpen = false,
  forceBubble = false,
}: SpotChatWidgetProps) {
  const location = useLocation();
  const titleId = useId();
  const [open, setOpen] = useState(defaultOpen);
  const [dismissed, setDismissed] = useState(false);
  const [bubbleVisible, setBubbleVisible] = useState(forceBubble);
  const [bubbleIndex, setBubbleIndex] = useState(0);
  const [bubbleLeaving, setBubbleLeaving] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<UiMessage[]>([
    {
      role: 'assistant',
      content: 'Hey, I am Spot. Ask me about BotSpot courses, pricing, or how to get started.',
    },
  ]);
  const [isSending, setIsSending] = useState(false);
  const sessionIdRef = useRef(createSessionId());
  const listRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const hideOnPath = useMemo(
    () => !isSpotAllowedPath(location.pathname || ''),
    [location.pathname],
  );

  const delayMs = bubbleDelayMs ?? getSpotBubbleDelayMs();

  useEffect(() => {
    if (forceBubble || open || hideOnPath) return undefined;
    const timer = window.setTimeout(() => setBubbleVisible(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, forceBubble, hideOnPath, open]);

  // Cycle the bubble invitations: show ~6s, animate out, swap, animate in.
  useEffect(() => {
    if (!bubbleVisible || open) return undefined;
    const timers: number[] = [];
    timers.push(
      window.setTimeout(() => {
        setBubbleLeaving(true);
        timers.push(
          window.setTimeout(() => {
            setBubbleLeaving(false);
            setBubbleIndex((i) => (i + 1) % SPOT_BUBBLE_MESSAGES.length);
          }, 380),
        );
      }, 6000),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [bubbleVisible, open, bubbleIndex]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const openPanel = useCallback(() => {
    setOpen(true);
    setBubbleVisible(false);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
  }, []);

  const dismissWidget = useCallback(() => {
    setDismissed(true);
    setOpen(false);
    setBubbleVisible(false);
    abortRef.current?.abort();
    setIsSending(false);
  }, []);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isSending) return;

    const historyForApi = messages
      .filter((m): m is SpotChatMessage => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));

    setInput('');
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true },
    ]);
    setIsSending(true);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    let streamed = '';
    try {
      await streamSpotChat({
        message: text,
        history: historyForApi,
        pagePath: `${location.pathname}${location.search || ''}`,
        siteOrigin: window.location.origin,
        pageSnapshot: collectVisiblePageSnapshot(),
        sessionId: sessionIdRef.current,
        signal: controller.signal,
        handlers: {
          onDelta: (delta) => {
            streamed += delta;
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = {
                  role: 'assistant',
                  content: streamed,
                  streaming: true,
                };
              }
              return next;
            });
          },
          onFinal: (content) => {
            const finalText =
              (content || streamed).trim() || 'I am Spot. Ask me another question about BotSpot.';
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: 'assistant', content: finalText };
              return next;
            });
          },
          onError: (message) => {
            setMessages((prev) => {
              const next = [...prev];
              // Replace empty streaming bubble with error.
              const lastMessage = next[next.length - 1];
              if (lastMessage?.role === 'assistant' && !lastMessage.content) {
                next[next.length - 1] = { role: 'error', content: message };
              } else {
                next.push({ role: 'error', content: message });
              }
              return next;
            });
          },
        },
      });
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        setMessages((prev) => [
          ...prev.filter(
            (m) => !(m.role === 'assistant' && 'streaming' in m && m.streaming && !m.content),
          ),
          {
            role: 'error',
            content: 'Spot is temporarily unavailable. Try again in a moment.',
          },
        ]);
      }
    } finally {
      setIsSending(false);
    }
  }, [input, isSending, location.pathname, location.search, messages]);

  if (hideOnPath || dismissed) {
    return null;
  }

  return (
    <div
      className="spot-chat-root"
      data-testid="spot-chat-widget"
      data-spot-open={open ? 'true' : 'false'}
    >
      {!open && (
        <div className="spot-chat-launcher-shell">
          <button
            type="button"
            className="spot-chat-launcher"
            data-testid="spot-chat-launcher"
            aria-label="Chat with Spot"
            onClick={openPanel}
          >
            {bubbleVisible && (
              <span
                key={bubbleIndex}
                className={['spot-chat-bubble', bubbleLeaving ? 'spot-chat-bubble--leaving' : '']
                  .filter(Boolean)
                  .join(' ')}
                data-testid="spot-chat-bubble"
              >
                <span className="spot-chat-bubble__title">Spot</span>
                <span className="spot-chat-bubble__text">{SPOT_BUBBLE_MESSAGES[bubbleIndex]}</span>
              </span>
            )}
            <span className="spot-chat-avatar" aria-hidden="true">
              <img src={spotCircle} alt="" />
            </span>
          </button>
          <button
            type="button"
            className="spot-chat-dismiss"
            aria-label="Dismiss Spot chat until page refresh"
            data-testid="spot-chat-dismiss"
            onClick={dismissWidget}
          >
            ×
          </button>
        </div>
      )}

      {open && (
        <section
          className="spot-chat-panel"
          data-testid="spot-chat-panel"
          aria-labelledby={titleId}
        >
          <header className="spot-chat-panel__header">
            <img src={spotCircle} alt="" />
            <div className="spot-chat-panel__header-copy">
              <h2 id={titleId} className="spot-chat-panel__title">
                Talk to Spot
              </h2>
              <p className="spot-chat-panel__subtitle">Ask about courses, pricing, and BotSpot</p>
            </div>
            <button
              type="button"
              className="spot-chat-panel__close"
              aria-label="Close Spot chat panel"
              data-testid="spot-chat-close"
              onClick={closePanel}
            >
              ×
            </button>
          </header>

          <div className="spot-chat-messages" ref={listRef} data-testid="spot-chat-messages">
            {messages.map((msg, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${msg.role}-${index}`}
                className={[
                  'spot-chat-msg',
                  msg.role === 'user'
                    ? 'spot-chat-msg--user'
                    : msg.role === 'error'
                      ? 'spot-chat-msg--error'
                      : 'spot-chat-msg--assistant',
                ].join(' ')}
              >
                {msg.content ? (
                  renderMessageContent(msg.content)
                ) : msg.role === 'assistant' ? (
                  <span
                    className="spot-chat-thinking"
                    data-testid="spot-chat-thinking"
                    aria-label="Spot is thinking"
                  >
                    <span className="spot-chat-thinking__label">Spot is thinking</span>
                    <span className="spot-chat-thinking__dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                  </span>
                ) : (
                  ''
                )}
              </div>
            ))}
          </div>

          <form
            className="spot-chat-composer"
            onSubmit={(event) => {
              event.preventDefault();
              void sendMessage();
            }}
          >
            <textarea
              data-testid="spot-chat-input"
              value={input}
              rows={2}
              placeholder="Ask Spot anything about BotSpot…"
              disabled={isSending}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void sendMessage();
                }
              }}
            />
            <button
              type="submit"
              className="spot-chat-composer__send"
              data-testid="spot-chat-send"
              disabled={isSending || !input.trim()}
              title={!input.trim() ? 'Type a question first' : 'Send to Spot'}
            >
              {isSending ? '…' : 'Send'}
            </button>
          </form>
        </section>
      )}
    </div>
  );
}
